import React, { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { Icon } from '@zydalabs/storefront-components';
import { Button } from '@zydalabs/zac-react';
import { Marker, useMapEvents, useMap } from 'react-leaflet';
import leaflet, { LatLngTuple } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';

import { IcGeoLocation } from 'common/icons';
import { CoordsType } from 'common/types';
import { debounce } from 'common/utils';
import { OPEN_STREET_MAPS_LINK } from 'common/constants';
import ServiceConfigs from 'service/config';
import { ErrorsConfig } from 'common/lib/errorHandler';
import AutoComplete from './AutoComplete';
import AutoCompleteNominatim from './AutoCompleteNominatim';
import { StyledAutoCompleteWrapper, StyledCurrLocationButtonWrapper, StyledMapTileWrapper } from './style';
import { CanvasTileLayer, addZoomControl } from './utils';

type MapTileTypes = {
  originalCenter: CoordsType;
  center: LatLngTuple;
  setCenter: Dispatch<SetStateAction<[number, number]>>;
  onDragEnd: (coords: { lat: number; lng: number }) => void;
  hasAutoComplete: boolean;
  hasZoomControl: boolean;
  hasCurrLocationButton: boolean;
  isBlackAndWhite: boolean;
  isStatic: boolean;
  zoom: number;
};

const MapTile: React.FC<MapTileTypes> = ({
  originalCenter,
  center,
  setCenter,
  onDragEnd,
  hasAutoComplete,
  hasZoomControl,
  hasCurrLocationButton,
  isBlackAndWhite,
  isStatic,
  zoom,
}) => {
  const { t } = useTranslation('common');
  const leafletMap = useMap();
  const router = useRouter();
  const googleMapsApiKey = ServiceConfigs.getGoogleMapsApiKey();
  const hasGoogleMapsApiKey = !!googleMapsApiKey;
  const isCheckoutPage = router.isReady && (router.pathname === '/checkout' || router.asPath.includes('/checkout'));
  const shouldShowLocationButton = !isCheckoutPage;

  const debouncedOnDragEnd = useMemo(
    () =>
      debounce((coords: CoordsType) => {
        if (onDragEnd) onDragEnd(coords);
      }, 250),
    [onDragEnd],
  );

  const changeCenter = (coords: CoordsType) => {
    setCenter([coords.lat, coords.lng]);
    debouncedOnDragEnd({ lat: coords.lat, lng: coords.lng });
  };

  useMapEvents({
    drag: e => changeCenter(e.target.getCenter()),
  });

  useEffect(() => {
    if (isBlackAndWhite) {
      const canvasTileLayer = new CanvasTileLayer();

      canvasTileLayer.addTo(leafletMap);
    } else {
      leaflet
        .tileLayer(`${OPEN_STREET_MAPS_LINK}/{z}/{x}/{y}.png`, {
          maxZoom: 20,
        })
        .addTo(leafletMap);
    }

    if (hasZoomControl) addZoomControl(leafletMap);
  }, []);

  useEffect(() => {
    if (isStatic) {
      setCenter([originalCenter.lat, originalCenter.lng]);
      leafletMap.setView([originalCenter.lat, originalCenter.lng], zoom);
    }
  }, [originalCenter]);

  return (
    <StyledMapTileWrapper>
      {hasAutoComplete && (
        <StyledAutoCompleteWrapper>
          {hasGoogleMapsApiKey ? (
            <AutoComplete
              setCenter={searchCenter => {
                changeCenter({ lat: searchCenter[0], lng: searchCenter[1] });
                leafletMap.setView(searchCenter, 16);
              }}
            />
          ) : (
            <AutoCompleteNominatim
              setCenter={searchCenter => {
                changeCenter({ lat: searchCenter[0], lng: searchCenter[1] });
                leafletMap.setView(searchCenter, 16);
              }}
            />
          )}
        </StyledAutoCompleteWrapper>
      )}

              {shouldShowLocationButton && (
          <StyledCurrLocationButtonWrapper>
            <Button
              variant="tertiary"
              startIcon={<Icon icon={IcGeoLocation} />}
              onClick={() => {
                leafletMap
                  .locate()
                  .on('locationfound', e => {
                    changeCenter(e.latlng);
                    leafletMap.setView([e.latlng.lat, e.latlng.lng], 16);
                  })
                  .on('locationerror', () => {
                    ErrorsConfig?.onSnackBarError({
                      show: true,
                      autoHide: true,
                      message: t('locationAccessDenied'),
                      onHide: () => ErrorsConfig?.onSnackBarError({ show: false }),
                    });
                  });
              }}
            />
          </StyledCurrLocationButtonWrapper>
        )}

      <Marker
        position={center}
        icon={leaflet.icon({
          iconUrl: '/map-marker.svg',
          iconSize: [50, 50],
        })}
      />
    </StyledMapTileWrapper>
  );
};

export default MapTile;
